import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { ModalController } from '@ionic/angular';
// Components
import { HTTPResponseModalComponent } from '../components/http-response-modal/http-response-modal.component';
// Models
import { User, Pilot, Vehicle, MultipleApiResponse } from '@mocca-technologies/model';
import { environment } from 'src/environments/environment';
import { DELETE_SUBOPERATOR, GET_ALL_PILOT, GET_ALL_SUBOPERATOR, GET_ALL_VEHICLE, GET_USER_SESSIONS, GET_VEHICLE_SESSIONS, OPERATOR, OPERATOR_ENROLMENT, PILOT, PILOT_ENROLMENT, PILOT_ROLES, PILOT_TRANSFER, SESSION, SESSION_ENROLMENT, VEHICLE, VEHICLE_ENROLMENT, VEHICLE_TRANSFER } from '../utils/routing/routes.model';

@Injectable({
  providedIn: 'root'
})
export class DroneOperatorService {

  user = JSON.parse(localStorage.getItem('user')) as User;

  _vehicles: Vehicle[];

  _pilots: Pilot[];

  _pilot: Pilot;

  _vehicle: Vehicle;

  constructor(private http:HttpClient,
              private modalController: ModalController
  ) {

  }

  /**
   *
   * @param id Unique identifier of the drone operator
   * @returns the drone operator
   */
  getDroneOperator(id: string): Observable<HttpResponse<MultipleApiResponse>> {
    const httpParams = new HttpParams().set('operatorId',id);
    return this.http.get<MultipleApiResponse>(OPERATOR, {
      params: httpParams,
      withCredentials: true,
      observe: 'response',
    }).pipe(
      tap((res: HttpResponse<MultipleApiResponse>) => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   *
   * @param operator json with the updated information of the drone operator
   * @returns an observable of the updated drone operator
   */
  editOperator(operator: any) {
    !environment.production && console.log('[DroneOperatorService] PUT', OPERATOR, { operator });
    return this.http.put<MultipleApiResponse>(
      OPERATOR,
      operator,
      {
        withCredentials: true,
        observe: 'response',
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   *
   * @param operator json with the information of the drone operator
   * @returns an observable of the created drone operator
   */
  createSubOperator(operator: any) {
    !environment.production && console.log('[DroneOperatorService] POST', OPERATOR, { operator });
    return this.http.post<MultipleApiResponse>(
      OPERATOR,
      operator,
      {
        observe: 'response',
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res.body;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

    /**
   *
   * @param operator json with the information of the drone operator
   * @returns an observable of the created drone operator
   */
  createOperator(operator: any) {
    !environment.production && console.log('[DroneOperatorService] POST', OPERATOR_ENROLMENT, { operator });
    return this.http.post<MultipleApiResponse>(
      OPERATOR_ENROLMENT,
      operator,
      {
        withCredentials: true,
        observe: 'response',
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res.body;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

    /**
   *
   * @param operatorUuid unique identifier of the drone operator
   * @returns an observable of the deleted drone operator
   */
  deleteEnrolmentOperator(operatorUuid: string) {
    !environment.production && console.log('[DroneOperatorService] DELETE', OPERATOR_ENROLMENT, { operatorId: operatorUuid });
    const httpParams = new HttpParams().set('operatorId', operatorUuid);

    return this.http.delete<MultipleApiResponse>(
      OPERATOR_ENROLMENT,
      {
        observe: 'response',
        params: httpParams
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res.body;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

      /**
   *
   * @param operatorUuid unique identifier of the drone operator
   * @returns an observable of the deleted drone operator
   */
  deleteEnrolmentPilot(pilotUuid: string, operatorId: string) {
    !environment.production && console.log('[DroneOperatorService] DELETE', PILOT_ENROLMENT, { pilotId: pilotUuid, operatorId });
    const httpParams = new HttpParams().set('pilotId', pilotUuid).set('operatorId', operatorId);

    return this.http.delete<MultipleApiResponse>(
      PILOT_ENROLMENT,
      {
        observe: 'response',
        params: httpParams
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res.body;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   *
   * @param operatorUuid unique identifier of the drone operator
   * @returns an observable of the deleted drone operator
   */
  deleteOperator(operatorUuid: string) {
    !environment.production && console.log('[DroneOperatorService] DELETE', OPERATOR, { operatorId: operatorUuid });
    const httpParams = new HttpParams().set('operatorId', operatorUuid);
    return this.http.delete<MultipleApiResponse>(
      OPERATOR,
      {
        observe: 'response',
        params: httpParams
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res.body;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   *
   * @param operatorId unique identifier of the drone operator
   * @returns an observable of the sub-operators
   */
  getSubOperators(operatorId: string): Observable<HttpResponse<MultipleApiResponse>> {
    !environment.production && console.log('[DroneOperatorService] GET', GET_ALL_SUBOPERATOR, { organizationUuid: operatorId });
    const httpParams = new HttpParams().set('organizationUuid', operatorId);
    return this.http.get<MultipleApiResponse>(GET_ALL_SUBOPERATOR, {
      withCredentials: true,
      observe: 'response',
      params: httpParams,
    }).pipe(
      tap((res: HttpResponse<MultipleApiResponse>) => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a sub-operator. If pilots or vehicles are enrolled in the sub-operator, they will be transferred to the main operator.
   * @param organizationUuid
   * @returns
   */
  deleteSubOperator(organizationUuid: string) {
    !environment.production && console.log('[DroneOperatorService] DELETE', DELETE_SUBOPERATOR, { organizationUuid });
    const httpParams = new HttpParams().set('organizationUuid', organizationUuid);
    return this.http.delete<MultipleApiResponse>(
      DELETE_SUBOPERATOR,
      {
        withCredentials: true,
        observe: 'response',
        params: httpParams,
      }).pipe(
        tap((res: HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  /**
   *
   * @param operatorId unique identifier of the drone operator
   * @param userId unique identifier of the user
   * @returns an observable of the user added to the operator
   */
  addUserToOperator(operatorId: string, userId: string) {
    !environment.production && console.log('[DroneOperatorService] PUT', PILOT_TRANSFER, { operatorId, userId });
    const httpParams = new HttpParams().set('operatorId', operatorId).set('userId', userId);
    return this.http.put<MultipleApiResponse>(
      PILOT_TRANSFER,
      null,
      {
        observe: 'response',
        params: httpParams,
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
    );
  }

  /**
   *
   * @param userId unique identifier of the user
   * @returns an observable of the deleted user
   */
  deleteUserFromOperator(userId: string) {
    !environment.production && console.log('[DroneOperatorService] DELETE', PILOT_TRANSFER, { userId });
    const httpParams = new HttpParams().set('userId', userId);
    return this.http.delete<MultipleApiResponse>(
      PILOT_TRANSFER,
      {
        observe: 'response',
        params: httpParams,
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
    );
  }

  /**
   *
   * @param operatorId unique identifier of the drone operator
   * @param vehicleId unique identifier of the vehicle
   * @returns an observable of the vehicle added to the operator
   */
  addVehicleToOperator(operatorId: string, vehicleId: string) {
    !environment.production && console.log('[DroneOperatorService] PUT', VEHICLE_TRANSFER, { operatorId, vehicleId });
    const httpParams = new HttpParams().set('operatorId', operatorId).set('vehicleId', vehicleId);
    return this.http.put<MultipleApiResponse>(
      VEHICLE_TRANSFER,
      null,
      {
        observe: 'response',
        params: httpParams,
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
    );
  }

  /**
   *
   * @param vehicleId unique identifier of the vehicle
   * @returns an observable of the deleted vehicle
   */
  deleteVehicleFromOperator(vehicleId: string) {
    !environment.production && console.log('[DroneOperatorService] DELETE', VEHICLE_TRANSFER, { vehicleId });
    const httpParams = new HttpParams().set('vehicleId', vehicleId);
    return this.http.delete<MultipleApiResponse>(
      VEHICLE_TRANSFER,
      {
        observe: 'response',
        params: httpParams
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
    );
  }

  /**
   *
   * @param vehicleId unique identifier of the vehicle
   * @returns an observable of the deleted vehicle
   */
  deleteEnrolmentVehicle(vehicleId: string, operatorId: string) {
    !environment.production && console.log('[DroneOperatorService] DELETE', VEHICLE_ENROLMENT, { vehicleId, operatorId });
    const httpParams = new HttpParams().set('vehicleId', vehicleId).set('operatorId', operatorId);

    return this.http.delete<Vehicle>(
      VEHICLE_ENROLMENT,
      {
        observe: 'response',
        params: httpParams
      }).pipe(
        tap((res:any) => {
          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
    );
  }

  /**
   *
   * @param vehicleId unique identifier of the vehicle
   * @returns an observable of the deleted vehicle
   */
  deleteEnrolmentSession(vehicleId: string, operatorId: string) {
    !environment.production && console.log('[DroneOperatorService] DELETE', SESSION_ENROLMENT, { vehicleId, operatorId });
    const httpParams = new HttpParams().set('vehicleId', vehicleId).set('operatorId', operatorId);

    return this.http.delete<MultipleApiResponse>(
      SESSION_ENROLMENT,
      {
        observe: 'response',
        params: httpParams
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
    );
  }

  /**
   * Update the profile and/or role list of a pilot. Only available for admin users.
   * @param pilotId UUID of the pilot to update
   * @param body object with optional `profile` and/or `roleList`
   */
  updatePilotRoles(pilotId: string, body: { profile?: string; roleList?: { organizationUuid: string; roleType: string }[] }) {
    const httpParams = new HttpParams().set('pilotId', pilotId);
    return this.http.put<MultipleApiResponse>(
      PILOT_ROLES,
      body,
      {
        observe: 'response',
        withCredentials: true,
        params: httpParams,
      }
    ).pipe(
      tap((res: HttpResponse<MultipleApiResponse>) => res),
      catchError((err: HttpErrorResponse) => throwError(() => err)),
    );
  }

  /**
   *
   * @param body json with the information of the pilot
   * @returns the answer from the server
   */
  postPilot(body: any, operatorId?: string){
    !environment.production && console.log('[DroneOperatorService] POST', PILOT, { body, operatorId });
    if (operatorId) {
      const httpParams = new HttpParams().set('operatorId', operatorId);
      return this.http.post<MultipleApiResponse>(
        PILOT,

        body,
        {
          observe: 'response',
          withCredentials: true,
          params: httpParams,
        })
        .pipe(
          tap((res:HttpResponse<MultipleApiResponse>) => {
            return res;
          }),
          catchError((err: HttpErrorResponse) => {
            return throwError(() => err);
          }),
        );
    } else {
      return this.http.post<MultipleApiResponse>(
        PILOT,

        body,
        {
          observe: 'response',
          withCredentials: true,
        }
      ).pipe(
          tap((res:HttpResponse<MultipleApiResponse>) => {
            return res;
          }),
          catchError((err: HttpErrorResponse) => {
            return throwError(() => err);
          }),
        );
    }
  }

  /**
   * @param body json with the information of the pilot
   * @returns the answer from the server
   */
  createPilot(body: any, operatorId: string){
    !environment.production && console.log('[DroneOperatorService] POST', PILOT_ENROLMENT, { body, operatorId });
    const httpParams = new HttpParams().set('operatorId', operatorId);
    return this.http.post<MultipleApiResponse>(
      PILOT_ENROLMENT,
      body,
      {
        observe: 'response',
        withCredentials: true,
        params: httpParams,
      })
      .pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => err);
        }),
      );
  }

  /**
   *
   * @param body json with the updated information of the pilot
   * @param pilotID unique identifier of the pilot
   * @returns the answer from the server
   */
  editPilot(body: any, pilotID: string){
    !environment.production && console.log('[DroneOperatorService] PUT', PILOT, { body, pilotId: pilotID });
    const httpParams = new HttpParams().set('pilotId', pilotID);
    return this.http.put<MultipleApiResponse>(
      PILOT,
      body,
      {
        observe: 'response',
        withCredentials: true,
        params: httpParams,
      })
      .pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => err);
        }),
      );
  }

  /**
   *
   * @param body json with the information of the vehicle
   * @returns the answer from the server
   */
  postVehicle(body: any, operatorId?: string){
    !environment.production && console.log('[DroneOperatorService] POST', VEHICLE, { body, operatorId });
    if (operatorId) {
      const httpParams = new HttpParams().set('operatorId', operatorId);
      return this.http.post<MultipleApiResponse>(
        VEHICLE,

        body,
        {
          observe: 'response',
          withCredentials: true,
          params: httpParams,
        })
        .pipe(
          tap((res:HttpResponse<MultipleApiResponse>) => {
            return res;
          }),
          catchError((err: HttpErrorResponse) => {
            return throwError(() => err);
          }),
        );
    } else {
      return this.http.post<MultipleApiResponse>(
        VEHICLE,

        body,
        {
          observe: 'response',
          withCredentials: true,
        })
        .pipe(
          tap((res:HttpResponse<MultipleApiResponse>) => {
            return res;
          }),
          catchError((err: HttpErrorResponse) => {
            return throwError(() => err);
          }),
        );
      }
  }

  /**
   *
   * @param body json with the information of the vehicle
   * @returns the answer from the server
   */
  createVehicle(body: any, operatorId: string){
    !environment.production && console.log('[DroneOperatorService] POST', VEHICLE_ENROLMENT, { body, operatorId });
    const httpParams = new HttpParams().set('operatorId', operatorId);
    return this.http.post<MultipleApiResponse>(
      VEHICLE_ENROLMENT,
      body,
      {
        observe: 'response',
        withCredentials: true,
        params: httpParams,
      })
      .pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => err);
        }),
      );
  }

  /**
   *
   * @param body json with the updated information of the vehicle
   * @param vehicleID unique identifier of the vehicle
   * @returns the answer from the server
   */
  editVehicle(body: any, vehicleID: string){
    !environment.production && console.log('[DroneOperatorService] PUT', VEHICLE, { body, vehicleId: vehicleID, id: this.user.organizationUuid });
    const httpParams = new HttpParams().set('id', this.user.organizationUuid).set('vehicleId', vehicleID);
    return this.http.put<MultipleApiResponse>(
      VEHICLE,

      body,
      {
        observe: 'response',
        withCredentials: true,
        params: httpParams,
      })
      .pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => err);
        }),
      );
  }

  /**
   *
   * @returns all the vehicles of the drone operator
   */
  getAllVehicles(organizationUuid?: string): Observable<HttpResponse<MultipleApiResponse>> {
    !environment.production && console.log('[DroneOperatorService] GET', GET_ALL_VEHICLE, { organizationUuid });
    if (organizationUuid) {
      const httpParams = new HttpParams().set('organizationUuid', organizationUuid);
      return this.http.get<MultipleApiResponse>(GET_ALL_VEHICLE, {
        withCredentials: true,
        observe: 'response', // Ensure this is included
        params: httpParams,
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          this._vehicles = res?.body?.responseObject as Vehicle[];

          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
    } else {
      return this.http.get<MultipleApiResponse>(GET_ALL_VEHICLE, {
        withCredentials: true,
        observe: 'response', // Ensure this is included
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          this._vehicles = res?.body?.responseObject as Vehicle[];

          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
    }
  }

  /**
   *
   * @returns all the pilots of the drone operator
   */
  getAllPilots(organizationUuid?: string): Observable<HttpResponse<MultipleApiResponse>> {
    !environment.production && console.log('[DroneOperatorService] GET', GET_ALL_PILOT, { organizationUuid });
    if (organizationUuid) {
      const httpParams = new HttpParams().set('organizationUuid', organizationUuid);
      return this.http.get<MultipleApiResponse>(GET_ALL_PILOT, {
        withCredentials: true,
        observe: 'response', // Ensure this is included
        params: httpParams,
      }).pipe(
        tap((res: HttpResponse<MultipleApiResponse>) => {
          this._pilots = res?.body?.responseObject as Pilot[];
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
    } else {
      return this.http.get<MultipleApiResponse>(GET_ALL_PILOT, {
        withCredentials: true,
        observe: 'response', // Ensure this is included
      }).pipe(
        tap((res: HttpResponse<MultipleApiResponse>) => {
          this._pilots = res?.body?.responseObject as Pilot[];
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
    }
  }

  /**
   *
   * @param id unique identifier of the vehicle
   * @returns a specific vehicle
   */

  getVehicle(id: string, operatorId?: string): Observable<HttpResponse<MultipleApiResponse>> {
    !environment.production && console.log('[DroneOperatorService] GET', VEHICLE, { vehicleId: id, operatorId });
    const httpParams = new HttpParams().set('vehicleId', id);
    if (operatorId) {
      httpParams.set('operatorId', operatorId);
    }
    return this.http.get<MultipleApiResponse>(VEHICLE, {
      withCredentials: true,
      params:httpParams,
      observe: 'response',
    }).pipe(
      tap((res:HttpResponse<MultipleApiResponse>) => {
        this._vehicle = res?.body?.responseObject as Vehicle;

        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   *
   * @param id unique identifier of the pilot
   * @returns a specific pilot
   */
  getPilot(id: string): Observable<HttpResponse<MultipleApiResponse>> {
    !environment.production && console.log('[DroneOperatorService] GET', PILOT, { pilotId: id });
    const httpParams = new HttpParams().set('pilotId', id);
    return this.http.get<MultipleApiResponse>(PILOT, {
      withCredentials: true,
      params:httpParams,
      observe: 'response',
    }).pipe(
      tap((res: HttpResponse<MultipleApiResponse>) => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   *
   * @param vehicleId unique identifier of the vehicle
   * @returns the answer from the server
   */
  deleteVehicle(vehicleId: string){
    !environment.production && console.log('[DroneOperatorService] DELETE', VEHICLE, { vehicleId });
    const httpParams = new HttpParams().set('vehicleId', vehicleId);
    return this.http.delete<MultipleApiResponse>(
      VEHICLE,
      {
        observe: 'response',
        withCredentials: true,
        params: httpParams,
      })
      .pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   *
   * @param pilotId unique identifier of the pilot
   * @returns the answer from the server
   */
  deletePilot(pilotId: string){
    !environment.production && console.log('[DroneOperatorService] DELETE', PILOT, { pilotId });
    const httpParams = new HttpParams().set('pilotId', pilotId);
    return this.http.delete<MultipleApiResponse>(
      PILOT,
      {
        observe: 'response',
        withCredentials: true,
        params: httpParams,
      })
      .pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   * This method is used to return the sessions from the current user or a specific pilot.
   * @param pilotUuid
   * @returns
   */
  getUserSessions(pilotUuid?: string): Observable<HttpResponse<MultipleApiResponse>> {
    !environment.production && console.log('[DroneOperatorService] GET', GET_USER_SESSIONS, { userUuid: pilotUuid });
    if (pilotUuid) {
      const httpParams = new HttpParams().set('userUuid', pilotUuid);
      return this.http.get<MultipleApiResponse>(GET_USER_SESSIONS, {
        params: httpParams,
        withCredentials: true,
        observe: 'response',
      }).pipe(
        tap((res: HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
    } else {
      return this.http.get<MultipleApiResponse>(GET_USER_SESSIONS, {
        withCredentials: true,
        observe: 'response',
      }).pipe(
        tap((res: HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
    }
  }

  /**
   * This method is used to return the sessions from a vehicle.
   * @param pilotUuid
   * @returns
   */
  getVehicleSessions(vehicleUuid: string): Observable<HttpResponse<MultipleApiResponse>> {
    !environment.production && console.log('[DroneOperatorService] GET', GET_VEHICLE_SESSIONS, { vehicleUuid });
    const httpParams = new HttpParams().set('vehicleUuid', vehicleUuid);
    return this.http.get<MultipleApiResponse>(GET_VEHICLE_SESSIONS, {
      params: httpParams,
      withCredentials: true,
      observe: 'response',
    }).pipe(
      tap((res: HttpResponse<MultipleApiResponse>) => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  createSession(pilotId: string, vehicleId: string): Observable<HttpResponse<MultipleApiResponse>> {
    !environment.production && console.log('[DroneOperatorService] POST', SESSION, { pilotId, vehicleId });
    const httpParams = new HttpParams().set('pilotId', pilotId).set('vehicleId', vehicleId);

    return this.http.post<MultipleApiResponse>(
      SESSION,
      null,
      {
        observe: 'response',
        params: httpParams,
        withCredentials: true,
      }
    ).pipe(
      tap((res: HttpResponse<MultipleApiResponse>) => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  deleteSession(pilotId: string, vehicleId: string) {
    !environment.production && console.log('[DroneOperatorService] DELETE', SESSION, { pilotId, vehicleId });
    const httpParams = new HttpParams().set('pilotId', pilotId).set('vehicleId', vehicleId);
    return this.http.delete<MultipleApiResponse>(
      SESSION,
      {
        observe: 'response',
        params: httpParams,
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
    );
  }

  /**
   *
   * @param id unique identifier of the error
   * @param message of the error
   */
  async showServerError(id: string, message: string): Promise<void> {
    const modal = await this.modalController.create({
      component: HTTPResponseModalComponent,
      componentProps: {
        status: id,
        message: message
      },
      cssClass: 'http-response-modal'
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();
  }
}
